import {staticFile} from 'remotion';
import type {browserRenderPlan} from './browser-render-plan';
import {collectTtsAudioGroups} from '../compat/tts-audio-groups';

export type PitchAudioRequest = {src:string;rate:number;start:number;end:number;requiredAudio:boolean;boundaries?:number[]};
export type PreparedPitchAudio = PitchAudioRequest & {url:string|null};
export const MAX_PITCH_WINDOW_SECONDS = 120;
export const MAX_PITCH_JOB_BYTES = 128 * 1024 * 1024;

/** Source-clock windows only. Group/transition clocks stay in the original renderer. */
export function collectPitchAudioRequests(plan:ReturnType<typeof browserRenderPlan>):PitchAudioRequest[]{
  const {items,assets,transitions}=plan.props;
  const parents=new Map<string,string[]>();
  for(const item of Object.values(items))if(item.type==='group')for(const child of item.children)
    parents.set(child,[...(parents.get(child)??[]),item.id]);
  const requests:PitchAudioRequest[]=[];
  const ttsGroups=collectTtsAudioGroups(items,plan.props.tracks,plan.fps);
  const globalTtsIds=new Set(ttsGroups.flatMap(group=>group.items.map(item=>item.id)));
  for(const group of ttsGroups){
    const first=group.items[0]!,last=group.items.at(-1)!,rate=first.playbackRate;
    if(group.trackMuted||rate>=1)continue;
    if(rate<.25)throw Error('감속 음성은 0.25배부터 지원합니다. 속도를 확인해 주세요.');
    const asset=assets[group.assetId],src=asset?.remoteUrl||(asset?.staticPath?staticFile(asset.staticPath):'');
    const origin=first.audioStartFromInSeconds,end=origin+(last.from+last.durationInFrames-first.from)/plan.fps*rate;
    if(!src)throw Error('연속 음성의 원본을 다시 연결해 주세요.');
    if((end-Math.max(0,origin-.05))/rate>MAX_PITCH_WINDOW_SECONDS)throw Error('연속된 감속 음성이 너무 깁니다. 짧은 구간의 사본으로 나누어 출력해 주세요.');
    requests.push({src,rate,start:Math.max(0,origin-.05),end,requiredAudio:true,boundaries:[origin,end]});
  }
  for(const id of plan.pitchAudioItemIds){
    if(globalTtsIds.has(id))continue;
    const item=items[id];if(!item||(item.type!=='audio'&&item.type!=='video'))throw Error('감속 음성의 항목을 확인해 주세요.');
    const rate=item.playbackRate,asset=assets[item.assetId];
    if(!Number.isFinite(rate)||rate<.25||rate>=1)throw Error('감속 음성은 0.25배부터 1배 미만까지 지원합니다. 속도를 확인해 주세요.');
    const src=asset?.remoteUrl||(asset?.staticPath?staticFile(asset.staticPath):'');
    if(!src)throw Error('감속 음성의 원본을 다시 연결해 주세요.');
    const lineage=new Set<string>(),queue=[id];
    while(queue.length){const key=queue.pop()!;if(lineage.has(key))continue;lineage.add(key);queue.push(...(parents.get(key)??[]));}
    let before=.05,after=0;
    for(const edge of Object.values(transitions)){
      if(lineage.has(edge.targetItemId))before+=Math.abs(edge.targetExtendedAssetStartDelta??0)+(edge.durationInFrames+(edge.targetExtendedFrames??0))/plan.fps*rate;
      if(lineage.has(edge.sourceItemId))after+=(edge.durationInFrames+(edge.sourceExtendedFrames??0))/plan.fps*rate;
    }
    const origin=item.type==='audio'?item.audioStartFromInSeconds:item.videoStartFromInSeconds;
    const start=Math.max(0,origin-before),end=origin+item.durationInFrames/plan.fps*rate+after;
    if(!Number.isFinite(start)||!Number.isFinite(end)||end<=start||(end-start)/rate>MAX_PITCH_WINDOW_SECONDS)
      throw Error('감속 음성 구간이 너무 깁니다. 짧은 구간의 사본으로 나누어 출력하거나 속도를 1배로 바꿔 주세요.');
    requests.push({src,rate,start,end,requiredAudio:item.type==='audio',boundaries:[origin,origin+item.durationInFrames/plan.fps*rate]});
  }
  requests.sort((a,b)=>a.src.localeCompare(b.src)||a.rate-b.rate||a.start-b.start);
  const merged:PitchAudioRequest[]=[];
  for(const entry of requests){
    const last=merged.at(-1);
    if(last&&last.src===entry.src&&last.rate===entry.rate&&entry.start<=last.end){
      last.end=Math.max(last.end,entry.end);last.requiredAudio ||=entry.requiredAudio;
      last.boundaries=[...new Set([...(last.boundaries??[]),...(entry.boundaries??[])])].sort((a,b)=>a-b);
      if((last.end-last.start)/last.rate>MAX_PITCH_WINDOW_SECONDS)
        throw Error('연속된 감속 음성이 너무 깁니다. 짧은 구간의 사본으로 나누어 출력해 주세요.');
    }else merged.push({...entry});
  }
  return merged;
}

export function resolvePreparedPitchAudio(entries:readonly PreparedPitchAudio[],src:string,rate:number,start:number,end:number){
  const found=entries.find(entry=>entry.src===src&&entry.rate===rate&&entry.start<=start+1e-7&&entry.end>=end-1e-7);
  if(!found)throw Error('감속 음성의 준비된 구간이 맞지 않습니다. 원본과 속도를 확인한 뒤 다시 출력해 주세요.');
  return found;
}
